import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/data';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative block overflow-hidden rounded-lg cursor-pointer"
        onClick={handleClick}
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 明星黑料大全`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 mb-3">
            {article.category}
          </span>
          <h3 className="font-heading font-bold text-lg md:text-xl text-background-50 line-clamp-2 mb-2 group-hover:text-primary-300 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-background-50/70 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-background-50/60">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 100000 ? `${(article.views / 10000).toFixed(1)}万` : article.views.toLocaleString()}
            </span>
            <span>{article.date}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
        onClick={handleClick}
      >
        <div className="w-28 h-20 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 明星黑料大全`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-foreground-950 line-clamp-2 mb-1 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="text-primary-600 font-medium">{article.category}</span>
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views >= 100000 ? `${(article.views / 10000).toFixed(1)}万` : article.views.toLocaleString()}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
      onClick={handleClick}
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 明星黑料大全`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
            {article.category}
          </span>
          {article.isNew && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
              最新
            </span>
          )}
          {article.isHot && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
              热门
            </span>
          )}
        </div>
        <h3 className="font-heading font-bold text-base text-foreground-950 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{article.date}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views >= 100000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views.toLocaleString()}阅读`}
          </span>
        </div>
      </div>
    </Link>
  );
}